// The New call sheet: a name, AI transcription and who can join, then Start.
// Start calls createRoom (it inserts the row and merges it into root.rooms),
// lands on /meet and stamps the room hash.
//
// IMPORTANT: reactive getters below route through `_meetDraft(s)` so they
// never throw on the first-pass signal evaluation (when root state may
// not have `newRoomDraft` yet). Optional chaining on `s.root?.X` has
// still been observed to throw `Cannot read properties of undefined
// (reading 'newRoomDraft')` inside smbls v3.14 signal effects, which is
// why we explicitly guard with a try-block helper instead of relying on
// `?.` alone.
//
// Every control is the shared kit's: SwitchInput, AppSegmented and the kit
// capsules (frank-audit 2026-10-30: 14 errors, 5 warnings on the old body).
// The sheet used to draw its own switch and segments and paint them from a
// <style> tag it wrote into document.head, because the reactive props of the
// smbls of 2026-06 froze at mount. The kit keeps its state through props
// (aria-checked from `onCheck`, the chosen segment from `isActive`), so the
// tag, its `!important` rules and the hand-written aria-checked are gone.

// MEET-AUTOJOIN-1 related finding (WAVE-J audit, 2026-08-14): a truly empty
// name silently produced the literal string "Unnamed call" for every
// no-name submit — 86 rooms in the audited workspace shared that one name,
// making the room list impossible to scan. Derive a readable per-call
// default (date + time) instead of a shared placeholder string. This
// supersedes the 2026-06-12 "empty name is fine" note below Start — the
// 2026-08-14 audit is the more recent Nika-sourced direction, and deriving a
// name (not blocking Start) satisfies both: no friction, no more collisions.
// `el` first: the name is STORED, so it is written once, in the language of
// the person who starts the call (shared functions/localizedDate.js reads the
// session language; Chrome has no Georgian month names of its own).
const _deriveRoomName = (el) => {
  try {
    const d = new Date()
    return el
      .call('polyglot', 'meet.newRoom.derivedName')
      .replace('{date}', el.call('localizedDate', d, { month: 'short', day: 'numeric' }))
      .replace('{time}', el.call('localizedDate', d, { hour: 'numeric', minute: '2-digit' }))
  } catch (err) {
    return el.call('polyglot', 'meet.room.untitled')
  }
}

const _meetDraft = (s) => {
  try {
    if (!s) return {}
    const root = s.root
    if (!root) return {}
    return root.newRoomDraft || {}
  } catch (err) {
    console.warn('[MeetNewRoomModal] draft read failed', err)
    return {}
  }
}

const _meetPatch = (s, patch) => {
  try {
    if (!s || !s.root) return
    const current = s.root.newRoomDraft || {}
    s.root.update({ newRoomDraft: { ...current, ...patch } })
  } catch (err) {
    console.warn('[MeetNewRoomModal] patch failed', err)
  }
}

// Body-only component — backdrop, ESC binding, dismiss-on-backdrop are
// owned by @symbo.ls/windows ModalLayer (renders inside CustomTemplate
// Slot). Triggered via `el.call('openNewRoomModal')` and closed via
// `el.call('closeNewRoomModal')` — helpers in meet/functions/.
export const MeetNewRoomModal = {
  width: '100%',
  maxWidth: '100%',
  minWidth: '0',
  boxSizing: 'border-box',
  gap: 'Z1',
  // The windows ModalLayer content box is tight (see the Icons note below) —
  // an X (4px) side inset keeps children's rounded corners and the global
  // focus ring inside the clip instead of shearing at the modal edges
  // (2026-08-07 per Nika "fix overflow cutting its edges").
  padding: '0 X',

  ModalHeader: {
    // Neutralize the brand ModalHeader Icons bleed (`margin: '-Y2 -Z1 -
    // auto'`) — it compensates the brand ModalWindow's padding, but this
    // modal renders in the @symbo.ls/windows ModalLayer whose content box
    // is tight, so the Close button poked ~13px past the edge and forced a
    // horizontal scrollbar over the footer (2026-07-29 per Nika).
    Icons: { margin: '0 0 0 auto' },
    Hgroup: {
      H: { text: '{{ meet.newRoom.heading | polyglot }}' },
      P: {
        text: '{{ meet.newRoom.hint | polyglot }}'
      }
    }
  },

  // Primary field — no label chrome (the placeholder carries the affordance;
  // empty = a derived name). The shared field kit's control
  // (shared/components/AppField.js): AppInput owns the whole surface — the
  // brand `field` fill, the hairline edge, the control scale, the global
  // focus ring — so this key keeps the behaviour only.
  AppInput: {
    // A placeholder is NOT an accessible name (it vanishes on focus and isn't
    // reliably announced) — give the field an explicit label. The flat
    // `ariaLabel`: with no AppField parent it is the one form that replaces
    // the kit's parent-label name.
    type: 'text',
    ariaLabel: (el) => el.call('polyglot', 'meet.newRoom.nameAria'),
    autocomplete: 'off',
    placeholder: (el) => el.call('polyglot', 'meet.newRoom.namePlaceholder'),
    value: (el, s) => _meetDraft(s).name || '',
    onInput: (e, el, s) => _meetPatch(s, { name: e.target.value })
  },

  // The settings rows, on the sheet itself: content sits on a sheet unboxed,
  // and the rows part by their own padding, not by strokes (the room
  // settings sheet's rule, www-ee item 7). It was one rounded `overlay` card
  // (1.01:1 on the sheet, a box no one sees) with a hairline between the rows
  // in a raw CSS variable.
  SettingsCard: {
    flow: 'y',

    TranscribeRow: {
      flow: 'x',
      align: 'center space-between',
      gap: 'A',
      padding: 'Z1 0',

      Label: {
        flow: 'y',
        gap: 'W',
        flex: '1',
        Title: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'title',
          fontWeight: '500',
          text: '{{ meet.newRoom.transcription | polyglot }}'
        },
        Caption: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          text: '{{ meet.newRoom.transcriptionHint | polyglot }}'
        }
      },

      // The kit switch (shared components/SwitchInput.js): `onCheck` reads the
      // draft, and the switch writes its own aria-checked and paints each
      // state from it. The row's title names it.
      SwitchInput: {
        'aria-label': (el) => el.call('polyglot', 'meet.newRoom.transcription'),
        onCheck: (el, s) => !!_meetDraft(s).transcribeEnabled,
        onClick: (e, el, s) => {
          e.preventDefault()
          _meetPatch(s, { transcribeEnabled: !_meetDraft(s).transcribeEnabled })
        }
      }
    },

    VisibilityRow: {
      flow: 'y',
      gap: 'Z',
      padding: 'Z1 0',

      Head: {
        flow: 'x',
        align: 'center space-between',
        Title: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'title',
          fontWeight: '500',
          text: '{{ meet.newRoom.whoCanJoin | polyglot }}'
        }
      },

      // Who can join — the kit's segmented control in its `choice` strength
      // (a radiogroup, arrow keys, the ink thumb), full width. A pick goes
      // through the registered `meetSetNewRoomVisibility` (functions/
      // newRoomModal.js). Each row carries the value it was derived at, so a
      // pick re-derives the rows and every segment re-reads its own choice
      // (the chat Create sheet's pattern); each keeps its hint as the tooltip.
      AppSegmented: {
        variant: 'choice',
        width: '100%',
        ariaLabel: (el) => el.call('polyglot', 'meet.newRoom.whoCanJoin'),
        current: (el, s) => _meetDraft(s).visibility || 'workspace',
        select: 'meetSetNewRoomVisibility',
        childrenAs: 'state',
        children: (el, s) => {
          const chosen = _meetDraft(s).visibility || 'workspace'
          return [
            {
              key: 'private',
              text: 'meet.visibility.private',
              hint: 'meet.visibility.privateHint',
              value: 'private',
              chosen
            },
            {
              key: 'workspace',
              text: 'meet.visibility.workspace',
              hint: 'meet.visibility.workspaceHint',
              value: 'workspace',
              chosen
            },
            {
              key: 'public',
              text: 'meet.visibility.public',
              hint: 'meet.visibility.publicHint',
              value: 'public',
              chosen
            }
          ]
        },
        childProps: {
          flex: '1 1 auto',
          isActive: (el, s) => !!s && s.value === s.chosen,
          title: (el, s) => el.call('polyglot', (s && s.hint) || '')
        }
      },

      Caption: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        lineHeight: '1.45',
        text: (el, s) => {
          const v = _meetDraft(s).visibility || 'workspace'
          if (v === 'private') return el.call('polyglot', 'meet.visibility.privateCaption')
          if (v === 'public') return el.call('polyglot', 'meet.visibility.publicCaption')
          return el.call('polyglot', 'meet.visibility.workspaceCaption')
        }
      }
    }
  },

  ModalFooter: {
    flow: 'x',
    gap: 'Z1',
    align: 'center right',
    // Neutralize the brand ModalFooter's `margin: '0 -X'` full-bleed — that
    // negative margin compensates the brand ModalWindow's side padding, but
    // THIS modal renders in the @symbo.ls/windows ModalLayer whose content
    // box is narrower, so the footer overflowed ~8px and spawned a
    // horizontal scrollbar strip over the buttons ("overflow cutting the
    // button", 2026-07-29 per Nika).
    margin: 'Z 0 0',

    // The kit's ghost and ink capsules (shared AppField.js) own the control
    // height, the inset, the weight and the states; the call site set them by
    // hand before (Rule 68).
    AppCapsuleBtnGhost: {
      text: '{{ meet.newRoom.cancel | polyglot }}',
      onClick: (e, el) => {
        e.preventDefault()
        el.call('closeNewRoomModal')
      }
    },

    AppCapsuleBtnPrimary: {
      text: '{{ meet.newRoom.start | polyglot }}',
      onClick: async (e, el, s) => {
        const d = _meetDraft(s)
        // No Start-blocking friction, but no more shared "Unnamed call"
        // literal either — derive a distinguishable per-call name from the
        // creation time when the user left the field blank (see
        // _deriveRoomName above).
        const name = (d.name || '').trim() || _deriveRoomName(el)
        const id = await el.call('createRoom', {
          name,
          kind: 'room',
          visibility: d.visibility || 'workspace',
          transcribeEnabled: !!d.transcribeEnabled
        })
        if (!id) return
        if (s && s.root) s.root.update({ activeRoomId: id, meetView: 'call' })
        if (typeof window !== 'undefined') {
          // The modal opens from ANY route now (navbar NewCall button) —
          // land on /meet so the call view actually renders, then stamp
          // the shareable hash. meetView/activeRoomId are already set,
          // so the page mounts straight into the call. The workspace prefix
          // comes off through the registry (shared functions/
          // workspacePrefix.js), not a static import of the shared package.
          if (el.call('stripWorkspacePrefix', window.location.pathname) !== '/meet') {
            el.router('/meet', el.getRoot())
          }
          // Hash-only: the address keeps its path, the `/w/<org>/<workspace>`
          // that the route above (or the open /meet) carries (F2 — a bare
          // `/meet#…` here dropped it, and a reload opened the call in the
          // last-used workspace). Raw on purpose, as the shell's callPerson
          // stamps it: a second el.router would take over the pending /meet
          // mount while the shell's route-exit motion runs.
          // frank-allow FA410
          history.replaceState(null, '', `#room=${id}&view=call`)
        }
        el.call('connectCall', id)
      }
    }
  }
}
